import type { ReactNode } from 'react';

/**
 * Splits `body` on `**…**` markers and renders bold segments as `<b>` elements.
 * Unbalanced markers are rendered literally (graceful degradation).
 *
 * @param body - Notification body text, may contain **bold** markers.
 * @returns Array of React nodes (strings and `<b>` elements).
 */
export const renderRichBody = (body: string): ReactNode => {
  const parts = body.split('**');

  // Odd number of parts means balanced pairs exist; even means unbalanced.
  // With N "**" markers, split produces N+1 parts.
  // Balanced: markers come in pairs → parts count is odd (1 + 2k segments).
  if (parts.length % 2 === 0) {
    // Unbalanced — render literally
    return body;
  }

  return parts.map((part, index) => {
    if (index % 2 === 1) {
      // Odd indices are the bold segments
      return <b key={index}>{part}</b>;
    }

    return part;
  });
};

/**
 * Strips `**` bold markers from a body string, returning plain text.
 * Used by the native-notification path (Task 18) to avoid OS markup.
 *
 * @param body - Notification body text.
 * @returns Plain text without `**` markers.
 */
export const extractRichBodyText = (body: string): string => {
  return body.replaceAll('**', '');
};
